/**
 * usePrefetchOfflineData.ts
 *
 * Background prefetch hook — runs silently once per app session when the user
 * is authenticated and online.
 *
 * Fetches ALL customers and ALL products and stores them in IndexedDB so that
 * the create-order flow (customer picker + product picker) works fully offline.
 *
 * Rules:
 *  - Only runs if a valid token exists in localStorage (user is logged in)
 *  - Only runs if navigator.onLine is true (no point hitting the network offline)
 *  - Non-blocking: errors are swallowed silently — this must NEVER break the app
 *  - Runs once on mount, then again whenever the browser comes back online
 */

import { useEffect, useRef } from 'react';
import { getCustomers } from 'interfaces/customer';
import { getProducts } from 'interfaces/products';

const PREFETCH_INTERVAL_MS = 10 * 60 * 1000; // re-prefetch at most every 10 min

export const usePrefetchOfflineData = () => {
    const lastPrefetchRef = useRef<number>(0);

    const prefetch = async () => {
        if (!localStorage.getItem('token')) return;
        if (!navigator.onLine) return;

        const now = Date.now();
        if (now - lastPrefetchRef.current < PREFETCH_INTERVAL_MS) return;
        lastPrefetchRef.current = now;

        try {
            // Fetch recent customers for the offline cache.
            // Full-catalog search always goes server-side, so 500 is enough
            // to cover the create-order flow without a 5000-row payload at startup.
            await getCustomers();
        } catch {
            // Fail silently
        }

        try {
            // Fetch products page by page up to 500 items max.
            // getProducts paginates automatically (MAX_PRODUCTS_PAGE_SIZE = 500),
            // and any search term triggers a server-side query on the full catalog.
            await getProducts(1, 500);
        } catch {
            // Fail silently
        }
    };

    useEffect(() => {
        // Initial prefetch on mount
        prefetch();

        // Re-prefetch when the browser comes back online
        window.addEventListener('online', prefetch);
        return () => window.removeEventListener('online', prefetch);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
};
